package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusWithin
import com.varabyte.kobweb.silk.style.selectors.placeholder
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLTextAreaElement
import kotlin.random.Random

val LFieldLabelStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .letterSpacing(TypeScale.MicroTracking)
        .lineHeight(TypeScale.BodyLineHeight)
        .styleModifier { property("text-transform", "uppercase") }
}

val LTextFieldFrameStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(ControlHeight.MD)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            // Through the motion helper, not a hand-written string: a component that spells its
            // own `transition` out cannot be reached by a change to the motion system, which is
            // the entire reason the system exists.
            //
            // At the helper's default [Motion.FastMs], which the token names for "hover, focus,
            // and other in-place colour" changes, and not the [Motion.StandardMs] this used to
            // override it with. Focusing this field does two things at once: the 2px ring below
            // appears immediately, because `outline-width` is not a property any transition here
            // covers, and the border turns accent. At 180ms the border was still travelling long
            // after the ring had landed, so one keystroke's worth of focus arrived at two speeds.
            .transition("border-color", "box-shadow")
    }
    focusWithin {
        Modifier
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    // **No rule for `[data-invalid="true"]`, deliberately.** This frame used to take a `danger`
    // boundary while invalid, which is hard rule 4 — status colour never touches an interactive
    // control — and the specs say so twice more in this component's own words: usage-and-credits
    // "No hue on the field, no red border (rule 4)", and billing "`$p-accent`, not `$p-danger`".
    // The attribute stays, because it is what the surface tests assert on and what the error
    // sentence below is keyed to; only the paint is gone. `LDateRange` had already reasoned this
    // out for its own boundary — the error is carried by its sentence, where the word does the
    // work — and a field that says nothing but turns red is unreadable to the readers rule 4 is
    // written for.
}

val LTextFieldInputStyle = CssStyle {
    base {
        Modifier
            .flexGrow(1)
            .width(100.percent)
            .height(100.percent)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .color(colorMode.toSitePalette().textPrimary)
            .fontSize(TypeScale.BodySmall)
            .padding(leftRight = Space.MD)
            .styleModifier {
                property("border", "0")
                property("outline", "0")
                property("min-width", "0")
            }
    }
    placeholder {
        Modifier.color(colorMode.toSitePalette().textMuted)
    }
}

/**
 * The sentence under a field that says what is wrong with it.
 *
 * The entrance is applied at the call sites rather than here, and that is not arbitrary:
 * [enterRise] reads `prefers-reduced-motion` when the modifier is *built*, and a `CssStyle` is built
 * once at registration. Asking the question here would answer it for the whole session, for every
 * reader, from whatever the preference happened to be when the page loaded.
 *
 * The rise itself matches [LAlert], which made the same argument one level up: a message that
 * materialises silently under a field reads as having been there all along and been missed. It is
 * transform-only and does not run at all for a reader who asked for less movement, so nothing here
 * can delay or hide the reason a form was refused.
 */
val LFieldErrorStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().danger)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
}

val LTextAreaStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .minHeight(96.px)
            .maxHeight(320.px)
            .overflow(Overflow.Auto)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            .padding(Space.MD)
            .fontSize(TypeScale.BodySmall)
            .lineHeight(TypeScale.BodyLineHeight)
            .styleModifier {
                property("resize", "vertical")
                property("field-sizing", "content")
            }
            .transitionColors()
    }
    focusWithin {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    placeholder {
        Modifier.color(colorMode.toSitePalette().textMuted)
    }
    // No `[aria-invalid="true"]` paint. See LTextFieldFrameStyle: rule 4, and the error sentence
    // rendered under the control is the thing that says what is wrong.
}

@Composable
fun LTextField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true,
    trailing: (@Composable () -> Unit)? = null,
    inputType: InputType<String> = InputType.Text,
    autocomplete: String? = null,
) {
    val inputId = remember { "lingvo-field-${Random.nextInt(1_000_000)}" }
    val errorId = "$inputId-error"
    Column(
        LFieldGroupStyle.toModifier()
            .then(modifier)
            .attr("data-disabled", (!enabled).toString()),
    ) {
        if (label != null) {
            Label(forId = inputId, attrs = LFieldLabelStyle.toModifier().toAttrs()) {
                Text(label)
            }
        }
        Row(
            LTextFieldFrameStyle.toModifier()
                .attr("data-invalid", (error != null).toString()),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Input(
                type = inputType,
                attrs = LTextFieldInputStyle.toModifier().toAttrs {
                    id(inputId)
                    value(value)
                    placeholder(placeholder)
                    if (autocomplete != null) attr("autocomplete", autocomplete)
                    if (!enabled) disabled()
                    if (error != null) {
                        attr("aria-invalid", "true")
                        attr("aria-describedby", errorId)
                    }
                    onInput { onValueChange(it.value) }
                },
            )
            if (trailing != null) {
                Div(
                    Modifier
                        .color(ColorMode.current.toSitePalette().textMuted)
                        .padding(right = Space.SM)
                        .toAttrs(),
                ) {
                    trailing()
                }
            }
        }
        if (error != null) {
            Div(LFieldErrorStyle.toModifier().enterRise().toAttrs {
                id(errorId)
                attr("role", "alert")
            }) {
                Text(error)
            }
        }
    }
}

@Composable
fun LTextArea(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true,
) {
    val inputId = remember { "lingvo-area-${Random.nextInt(1_000_000)}" }
    val errorId = "$inputId-error"
    LaunchedEffect(inputId, value) {
        (document.getElementById(inputId) as? HTMLTextAreaElement)?.resizeToContent()
    }
    Column(
        LFieldGroupStyle.toModifier()
            .then(modifier)
            .attr("data-disabled", (!enabled).toString()),
    ) {
        if (label != null) {
            Label(forId = inputId, attrs = LFieldLabelStyle.toModifier().toAttrs()) {
                Text(label)
            }
        }
        TextArea(
            value = value,
            attrs = LTextAreaStyle.toModifier()
                .toAttrs {
                    id(inputId)
                    placeholder(placeholder)
                    if (!enabled) disabled()
                    if (error != null) {
                        attr("aria-invalid", "true")
                        attr("aria-describedby", errorId)
                    }
                    onInput { event ->
                        val element = event.target
                        element.resizeToContent()
                        onValueChange(event.value)
                    }
                },
        )
        if (error != null) {
            Div(LFieldErrorStyle.toModifier().enterRise().toAttrs {
                id(errorId)
                attr("role", "alert")
            }) {
                Text(error)
            }
        }
    }
}

private fun HTMLTextAreaElement.resizeToContent() {
    style.setProperty("height", "auto")
    style.setProperty("height", "${scrollHeight}px")
}
